import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { CheckCircle2, LoaderCircle, Mail } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { authFeatureService } from '../services/auth.feature.service'
import { authErrorMessage, isUnknownPasswordResetAccount } from '../lib/authErrorMessage'

interface PasswordResetValues {
  email: string
}

interface PasswordResetDialogProps {
  email: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function PasswordResetDialog({ email, open, onOpenChange }: PasswordResetDialogProps) {
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [requestError, setRequestError] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PasswordResetValues>({ mode: 'onBlur' })

  useEffect(() => {
    if (!open) return
    reset({ email })
    setLoading(false)
    setSent(false)
    setRequestError(null)
  }, [email, open, reset])

  async function sendReset(values: PasswordResetValues) {
    setLoading(true)
    setRequestError(null)
    try {
      await authFeatureService.sendPasswordReset(values.email.trim())
      setSent(true)
    } catch (error) {
      // Account existence is intentionally never disclosed by this screen.
      if (isUnknownPasswordResetAccount(error)) {
        setSent(true)
      } else {
        setRequestError(authErrorMessage(error, 'password-reset'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {sent ? (
          <>
            <DialogHeader className="items-center text-center sm:text-center">
              <div className="flex size-12 items-center justify-center rounded-full bg-brand-blue-light text-brand-black">
                <CheckCircle2 className="size-6" aria-hidden="true" />
              </div>
              <DialogTitle>Check your inbox</DialogTitle>
              <DialogDescription className="max-w-sm">
                If an account matches that email, we sent a secure password-reset link. It may take
                a minute to arrive.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="sm:justify-center">
              <Button type="button" onClick={() => onOpenChange(false)}>
                Back to sign in
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={(event) => void handleSubmit(sendReset)(event)} className="space-y-5">
            <DialogHeader>
              <div className="mb-1 flex size-10 items-center justify-center rounded-full bg-brand-orange-light text-brand-black">
                <Mail className="size-5" aria-hidden="true" />
              </div>
              <DialogTitle>Reset your password</DialogTitle>
              <DialogDescription>
                Enter your account email and we will send you a secure recovery link.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <Label htmlFor="reset-email">Email address</Label>
              <Input
                id="reset-email"
                type="email"
                autoComplete="email"
                autoFocus
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? 'reset-email-error' : undefined}
                {...register('email', {
                  required: 'Enter your email address.',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Enter a valid email address.',
                  },
                })}
              />
              {errors.email && (
                <p id="reset-email-error" className="text-sm text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>

            {requestError && (
              <Alert variant="destructive">
                <AlertDescription>{requestError}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && (
                  <LoaderCircle
                    className="size-4 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                )}
                {loading ? 'Sending…' : 'Send recovery link'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
